import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDark(prefersDark);

    // Listen for theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  return (
    <div className={`app ${isDark ? 'dark' : 'light'}`}>
      <div className="container">
        <header className="header">
          <h1 className="title">Collision</h1>
          <button className="theme-toggle" onClick={toggleTheme}>
            {isDark ? '☀️' : '🌙'}
          </button>
        </header>

        <main className="content">
          <div className="card">
            <h2>Privacy Policy</h2>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>

            <section className="section">
              <h3>1. Introduction</h3>
              <p>
                Welcome to Collision ("we," "our," or "us"). We are committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                when you use our mobile application.
              </p>
            </section>

            <section className="section">
              <h3>2. Information We Collect</h3>
              <h4>2.1 Information You Provide</h4>
              <p>We may collect information that you provide directly to us, including:</p>
              <ul>
                <li>Game scores and achievements</li>
                <li>Settings preferences (theme, audio settings)</li>
                <li>In-app purchase information</li>
              </ul>

              <h4>2.2 Automatically Collected Information</h4>
              <p>When you use our app, we may automatically collect certain information, including:</p>
              <ul>
                <li>Device information (device type, operating system, unique device identifiers)</li>
                <li>Usage data (gameplay statistics, session duration)</li>
                <li>Performance data (crash reports, error logs)</li>
              </ul>

              <h4>2.3 Third-Party Services</h4>
              <p>We use the following third-party services that may collect information:</p>
              <ul>
                <li><strong>Google AdMob:</strong> For displaying advertisements. AdMob may collect device identifiers and usage data for ad personalization.</li>
                <li><strong>Firebase:</strong> For analytics, crash reporting, and leaderboard services. Firebase may collect device information and usage analytics.</li>
                <li><strong>Apple App Store:</strong> For in-app purchases on iOS devices. Payment information is processed by Apple and not collected by us.</li>
                <li><strong>Google Play Store:</strong> For in-app purchases on Android devices. Payment information is processed by Google and not collected by us.</li>
              </ul>
            </section>

            <section className="section">
              <h3>3. In-App Purchases</h3>
              <p>
                Collision offers in-app purchases through the following platforms:
              </p>
              <ul>
                <li><strong>Apple App Store (iOS):</strong> In-app purchases are processed through Apple's payment system. All payment information is handled securely by Apple and we do not collect or store your payment details. Purchases are managed through your Apple ID account.</li>
                <li><strong>Google Play Store (Android):</strong> In-app purchases are processed through Google's payment system. All payment information is handled securely by Google and we do not collect or store your payment details. Purchases are managed through your Google account.</li>
              </ul>
              <p>
                When you make an in-app purchase, we receive confirmation of the purchase from the respective store,
                but we do not receive your payment information. You can manage your subscriptions and purchases
                through your device's store settings.
              </p>
            </section>

            <section className="section">
              <h3>4. How We Use Your Information</h3>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, maintain, and improve our game services</li>
                <li>Display personalized advertisements</li>
                <li>Maintain global leaderboards and rankings</li>
                <li>Process and verify in-app purchases</li>
                <li>Analyze usage patterns and improve game performance</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="section">
              <h3>5. Data Storage and Security</h3>
              <p>
                Your data is stored securely using Firebase services. We implement appropriate technical
                and organizational measures to protect your personal information. However, no method of
                transmission over the internet or electronic storage is 100% secure.
              </p>
              <p>
                Local game data (settings, local scores) is stored on your device and can be cleared
                by uninstalling the app.
              </p>
            </section>

            <section className="section">
              <h3>6. Data Sharing and Disclosure</h3>
              <p>We may share your information in the following circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> With third-party service providers (Google AdMob, Firebase) who assist in operating our app</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of assets</li>
                <li><strong>Leaderboards:</strong> Your game scores and username (if provided) may be displayed on public leaderboards</li>
              </ul>
            </section>

            <section className="section">
              <h3>7. Your Rights and Choices</h3>
              <p>You have the right to:</p>
              <ul>
                <li>Access your personal information stored in our systems</li>
                <li>Request deletion of your data</li>
                <li>Opt-out of personalized advertisements (via device settings)</li>
                <li>Clear local app data by uninstalling the app</li>
                <li>Contact us with privacy concerns</li>
              </ul>
            </section>

            <section className="section">
              <h3>8. Children's Privacy</h3>
              <p>
                Our app is not intended for children under the age of 13. We do not knowingly collect
                personal information from children under 13. If you believe we have collected information
                from a child under 13, please contact us immediately.
              </p>
            </section>

            <section className="section">
              <h3>9. International Data Transfers</h3>
              <p>
                Your information may be transferred to and processed in countries other than your country
                of residence. These countries may have data protection laws that differ from those in your
                country. By using our app, you consent to the transfer of your information to these countries.
              </p>
            </section>

            <section className="section">
              <h3>10. Changes to This Privacy Policy</h3>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by
                posting the new Privacy Policy on this page and updating the "Last updated" date. You are
                advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="section">
              <h3>11. Contact Us</h3>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="contact-info">
                Email: privacy@collision.app<br />
                Project: Collision (ball-game-b1d74)
              </p>
            </section>

            <section className="section">
              <h3>12. Third-Party Privacy Policies</h3>
              <p>For more information about how third-party services handle your data:</p>
              <ul>
                <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
                <li><a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase Privacy Policy</a></li>
                <li><a href="https://support.google.com/admob/answer/6128543" target="_blank" rel="noopener noreferrer">AdMob Privacy Policy</a></li>
                <li><a href="https://www.apple.com/privacy/" target="_blank" rel="noopener noreferrer">Apple Privacy Policy</a></li>
                <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Play Privacy Policy</a></li>
              </ul>
            </section>
          </div>
        </main>

        <footer className="footer">
          <p>&copy; {new Date().getFullYear()} Collision. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

export default App;

